'use memo'

import {
  AutopilotTaskDto,
} from '@urbanx/agx.activities.client'
import {
  Condition,
  Deposit,
  DepositType,
  FormCondition,
  StaffUpdateContractComplianceFieldsRequest,
  StaffUpdateContractDatesRequest,
  StaffUpdateContractSettlementRequest,
  SpecialConditionData,
  useGetCampaign,
  useGetSpecialConditionBank,
  useInvalidateUseStaffGetContractDates,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContractSettlement,
  useInvalidateUseStaffGetSaleInformation,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetContractDeposits,
  useStaffGetDocumentsForCampaign,
  useStaffGetContractDates,
  useStaffGetContractSettlement,
  useStaffGetSaleInformation,
  useStaffGetSpecialConditions,
  useStaffUpdateContractComplianceFields,
  useStaffUpdateContractDates,
  useStaffUpdateContractDeposits,
  useStaffUpdateContractSettlement,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import { isEqual } from 'lodash-es'
import TaskModalDialog, {
  type TaskModalContentConfig,
} from 'pages/allTasks/TaskModal/TaskModalDialog'
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ContractCompliancePage from './ContractCompliancePage'
import {
  filterContractDocumentGroups,
} from './ContractComplianceHelper'

// const hasModelValue = (model: ContractConditionDataPointModel | undefined) =>
//   Boolean(
//     model?.address ||
//     model?.dateInput ||
//     model?.numberInput != null ||
//     model?.textInput?.trim() ||
//     (model?.vendorIds?.length ?? 0) > 0 ||
//     (model?.numberOrDate &&
//       (model.numberOrDate.numberInput != null ||
//         model.numberOrDate.dateInput)) ||
//     (model?.numberOrQuestion &&
//       (model.numberOrQuestion.numberInput != null ||
//         model.numberOrQuestion.questionInput === true))
//   )

// const doesConfigRequireValue = (
//   config: ContractConditionDataPointConfig | undefined
// ) =>
//   Boolean(
//     config?.addressConfig ||
//     config?.dateInputConfig ||
//     config?.numberInputConditionConfig ||
//     config?.freeTextInputConfig ||
//     config?.vendorSelectorConfig ||
//     config?.numberOrDateConfig ||
//     config?.contractDatesConditionConfig ||
//     config?.numberOrQuestionConfig
//   )

// const isCustomSpecialConditionValid = (condition: SpecialConditionData) => {
//   const customCondition = condition.customCondition

//   if (!customCondition?.textInput?.trim()) return false

//   const clausesToCheck = [...(customCondition.subClauses ?? [])]

//   while (clausesToCheck.length > 0) {
//     const clause = clausesToCheck.shift()
//     if (!clause?.textInput?.trim()) return false
//     clausesToCheck.push(...(clause.subClauses ?? []))
//   }

//   return true
// }

// const areSpecialConditionsValid = (
//   specialConditions: SpecialConditionData[],
//   templates: SpecialConditionBankTemplate[] | undefined
// ) => {
//   if (specialConditions.length === 0) return false

//   return specialConditions.every((condition) => {
//     if (condition.conditionType === SpecialConditionDataConditionType.Custom) {
//       return isCustomSpecialConditionValid(condition)
//     }

//     const template = templates?.find(
//       (candidate) => candidate.conditionType === condition.conditionType
//     )

//     if (!template) return false

//     return (template.clauses ?? []).every((templateClause) => {
//       const selectedClause = condition.clauses?.find(
//         (clause) => clause.clauseId === templateClause.id
//       )

//       return (templateClause.dataPoints ?? []).every((templateDataPoint) => {
//         const selectedDataPoint = selectedClause?.dataPoints?.find(
//           (dataPoint) => dataPoint.dataPointId === templateDataPoint.id
//         )

//         if (!doesConfigRequireValue(templateDataPoint.config)) return true

//         return hasModelValue(selectedDataPoint?.model)
//       })
//     })
//   })
// }

const sanitizeFormConditionSpecialConditionReference = (
  formCondition: FormCondition | undefined,
  validSpecialConditionIds: Set<string>
) => {
  if (
    !formCondition ||
    formCondition.condition !== Condition.UponSatisfactionOfSpecialCondition ||
    !formCondition.specialConditionId ||
    validSpecialConditionIds.has(formCondition.specialConditionId)
  ) {
    return formCondition
  }

  return {
    ...formCondition,
    specialConditionId: undefined,
  }
}

const sanitizeDepositSpecialConditionReference = (
  deposit: Deposit | undefined,
  validSpecialConditionIds: Set<string>
) => {
  if (
    !deposit ||
    deposit.condition !== Condition.UponSatisfactionOfSpecialCondition ||
    !deposit.specialConditionId ||
    validSpecialConditionIds.has(deposit.specialConditionId)
  ) {
    return deposit
  }

  return {
    ...deposit,
    specialConditionId: undefined,
  }
}

interface ContractComplianceModalProps {
  campaignId: string
  isOpen: boolean
  onClose: () => void
  tasks: AutopilotTaskDto[]
}

const ContractComplianceModal = ({
  campaignId,
  isOpen,
  onClose,
  tasks,
}: ContractComplianceModalProps) => {
  const contractId = tasks.find((task) => task.contractId)?.contractId ?? ''

  const { data: campaign, isPending: isCampaignPending } = useGetCampaign(
    campaignId,
    !!campaignId
  )

  const { data: documentGroups, isPending: documentsPending } =
    useStaffGetDocumentsForCampaign(campaignId, !!campaignId && isOpen)

  const filteredDocuments = useMemo(
    () => filterContractDocumentGroups(documentGroups ?? []),
    [documentGroups]
  )

  const { data: contractDates, isPending: contractDatesPending } =
    useStaffGetContractDates(campaignId, contractId, !!contractId && isOpen)
  const { data: contractSettlement, isPending: contractSettlementPending } =
    useStaffGetContractSettlement(campaignId, contractId, !!contractId && isOpen)
  const { data: contractDeposits, isPending: contractDepositsPending } =
    useStaffGetContractDeposits(campaignId, contractId, !!contractId && isOpen)
  const { data: saleInformation, isPending: saleInformationPending } =
    useStaffGetSaleInformation(campaignId, contractId, !!contractId && isOpen)
  const {
    data: specialConditionsResponse,
    isPending: specialConditionsPending,
  } = useStaffGetSpecialConditions(
    campaignId,
    contractId,
    !!campaignId && !!contractId && isOpen
  )
  const {
    isPending: specialConditionBankPending,
  } = useGetSpecialConditionBank(isOpen)

  const [contractDate, setContractDate] = useState<Date | undefined>()
  const [contractDateDirty, setContractDateDirty] = useState(false)
  const [salePrice, setSalePrice] = useState<number | undefined>()
  const [salePriceDirty, setSalePriceDirty] = useState(false)
  const [initialDeposit, setInitialDeposit] = useState<Deposit | undefined>()
  const [balanceDeposit, setBalanceDeposit] = useState<Deposit | undefined>()
  const [depositsDirty, setDepositsDirty] = useState(false)
  // const [depositValidationError, setDepositValidationError] = useState(false)
  // const [financeConditionValidationError, setFinanceConditionValidationError] =
  //   useState(false)
  // const [buildingAndPestValidationError, setBuildingAndPestValidationError] =
  //   useState(false)
  // const [
  //   bodyCorporateRecordsValidationError,
  //   setBodyCorporateRecordsValidationError,
  // ] = useState(false)
  // const [settlementDateValidationError, setSettlementDateValidationError] =
  //   useState(false)
  const [subjectToFinance, setSubjectToFinance] = useState<FormCondition | undefined>()
  const [subjectToFinanceDirty, setSubjectToFinanceDirty] = useState(false)
  const [buildingAndPestCondition, setBuildingAndPestCondition] =
    useState<FormCondition | undefined>()
  const [buildingAndPestConditionDirty, setBuildingAndPestConditionDirty] =
    useState(false)
  const [bodyCorporateRecordsCondition, setBodyCorporateRecordsCondition] =
    useState<FormCondition | undefined>()
  const [bodyCorporateRecordsConditionDirty, setBodyCorporateRecordsConditionDirty] =
    useState(false)
  const [settlementInfo, setSettlementInfo] = useState<FormCondition | undefined>()
  const [settlementInfoDirty, setSettlementInfoDirty] = useState(false)
  const [specialConditions, setSpecialConditions] = useState<
    SpecialConditionData[]
  >([])
  const [specialConditionsDirty, setSpecialConditionsDirty] = useState(false)
  const [hasSpecialConditions, setHasSpecialConditions] = useState(false)

  const originalInitialDeposit = useMemo(
    () =>
      contractDeposits?.deposits?.find(
        (deposit) => deposit.depositType === DepositType.Initial
      ),
    [contractDeposits?.deposits]
  )
  const originalBalanceDeposit = useMemo(
    () =>
      contractDeposits?.deposits?.find(
        (deposit) => deposit.depositType === DepositType.Balance
      ),
    [contractDeposits?.deposits]
  )

  useEffect(() => {
    if (!contractDateDirty) setContractDate(contractDates?.contractDate)
  }, [contractDateDirty, contractDates?.contractDate])

  useEffect(() => {
    if (!salePriceDirty) setSalePrice(saleInformation?.salePrice ?? undefined)
  }, [saleInformation?.salePrice, salePriceDirty])

  useEffect(() => {
    if (!subjectToFinanceDirty) {
      setSubjectToFinance(contractDates?.subjectToFinance)
    }
  }, [contractDates?.subjectToFinance, subjectToFinanceDirty])

  useEffect(() => {
    if (!buildingAndPestConditionDirty) {
      setBuildingAndPestCondition(contractDates?.buildingAndPestCondition)
    }
  }, [buildingAndPestConditionDirty, contractDates?.buildingAndPestCondition])

  useEffect(() => {
    if (!bodyCorporateRecordsConditionDirty) {
      setBodyCorporateRecordsCondition(
        contractDates?.bodyCorporateRecordsCondition
      )
    }
  }, [
    bodyCorporateRecordsConditionDirty,
    contractDates?.bodyCorporateRecordsCondition,
  ])

  useEffect(() => {
    if (!settlementInfoDirty) {
      setSettlementInfo(contractSettlement?.settlementInfo)
    }
  }, [contractSettlement?.settlementInfo, settlementInfoDirty])

  useEffect(() => {
    if (depositsDirty) return

    setInitialDeposit(originalInitialDeposit)
    setBalanceDeposit(originalBalanceDeposit)
  }, [depositsDirty, originalBalanceDeposit, originalInitialDeposit])

  useEffect(() => {
    if (specialConditionsDirty) return

    const responseSpecialConditions =
      specialConditionsResponse?.specialConditionsData ?? []

    setSpecialConditions(responseSpecialConditions)
    setHasSpecialConditions(responseSpecialConditions.length > 0)
  }, [specialConditionsDirty, specialConditionsResponse?.specialConditionsData])

  const invalidateContractDates = useInvalidateUseStaffGetContractDates()
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement()
  const invalidateContractDeposits = useInvalidateUseStaffGetContractDeposits()
  const invalidateSaleInformation = useInvalidateUseStaffGetSaleInformation()
  const invalidateSpecialConditions =
    useInvalidateUseStaffGetSpecialConditions()

  const { staffUpdateContractComplianceFields } =
    useStaffUpdateContractComplianceFields({
      onSuccess: () => {},
      onError: () => {},
    })
  const { staffUpdateContractDates } = useStaffUpdateContractDates({
    onSuccess: () => {},
    onError: () => {},
  })
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  })
  const { staffUpdateContractSpecialConditions } =
    useStaffUpdateContractSpecialConditions({
      onSuccess: () => {},
      onError: () => {},
    })
  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  })

  const onContractDateUpdated = useCallback(
    (updatedContractDate: Date | undefined) => {
      setContractDate(updatedContractDate)
      setContractDateDirty(
        contractDates?.contractDate?.toString() !==
          updatedContractDate?.toString()
      )
    },
    [contractDates?.contractDate]
  )

  const onSalePriceUpdated = useCallback(
    (updatedSalePrice: number | undefined) => {
      setSalePrice(updatedSalePrice)
      setSalePriceDirty(
        (saleInformation?.salePrice ?? undefined) !== updatedSalePrice
      )
    },
    [saleInformation?.salePrice]
  )

  const onSpecialConditionsUpdated = useCallback(
    (updatedSpecialConditions: SpecialConditionData[]) => {
      const validSpecialConditionIds = new Set(
        updatedSpecialConditions
          .map((condition) => condition.id)
          .filter((conditionId): conditionId is string => Boolean(conditionId))
      )

      const nextSubjectToFinance =
        sanitizeFormConditionSpecialConditionReference(
          subjectToFinance,
          validSpecialConditionIds
        )
      const nextBuildingAndPestCondition =
        sanitizeFormConditionSpecialConditionReference(
          buildingAndPestCondition,
          validSpecialConditionIds
        )
      const nextBodyCorporateRecordsCondition =
        sanitizeFormConditionSpecialConditionReference(
          bodyCorporateRecordsCondition,
          validSpecialConditionIds
        )
      const nextSettlementInfo = sanitizeFormConditionSpecialConditionReference(
        settlementInfo,
        validSpecialConditionIds
      )
      const nextInitialDeposit = sanitizeDepositSpecialConditionReference(
        initialDeposit,
        validSpecialConditionIds
      )
      const nextBalanceDeposit = sanitizeDepositSpecialConditionReference(
        balanceDeposit,
        validSpecialConditionIds
      )

      setSpecialConditions(updatedSpecialConditions)
      setHasSpecialConditions(updatedSpecialConditions.length > 0)
      setSubjectToFinance(nextSubjectToFinance)
      setBuildingAndPestCondition(nextBuildingAndPestCondition)
      setBodyCorporateRecordsCondition(nextBodyCorporateRecordsCondition)
      setSettlementInfo(nextSettlementInfo)
      setInitialDeposit(nextInitialDeposit)
      setBalanceDeposit(nextBalanceDeposit)
      setSpecialConditionsDirty(
        !isEqual(
          specialConditionsResponse?.specialConditionsData ?? [],
          updatedSpecialConditions
        )
      )
      setSubjectToFinanceDirty(
        !isEqual(contractDates?.subjectToFinance, nextSubjectToFinance)
      )
      setBuildingAndPestConditionDirty(
        !isEqual(
          contractDates?.buildingAndPestCondition,
          nextBuildingAndPestCondition
        )
      )
      setBodyCorporateRecordsConditionDirty(
        !isEqual(
          contractDates?.bodyCorporateRecordsCondition,
          nextBodyCorporateRecordsCondition
        )
      )
      setSettlementInfoDirty(
        !isEqual(contractSettlement?.settlementInfo, nextSettlementInfo)
      )
      setDepositsDirty(
        !isEqual(originalInitialDeposit, nextInitialDeposit) ||
          !isEqual(originalBalanceDeposit, nextBalanceDeposit)
      )
    },
    [
      balanceDeposit,
      bodyCorporateRecordsCondition,
      buildingAndPestCondition,
      contractDates?.bodyCorporateRecordsCondition,
      contractDates?.buildingAndPestCondition,
      contractDates?.subjectToFinance,
      contractSettlement?.settlementInfo,
      initialDeposit,
      originalBalanceDeposit,
      originalInitialDeposit,
      settlementInfo,
      specialConditionsResponse?.specialConditionsData,
      subjectToFinance,
    ]
  )

  const onDepositsUpdated = useCallback(
    (updated: { initialDeposit?: Deposit; balanceDeposit?: Deposit }) => {
      setInitialDeposit(updated.initialDeposit)
      setBalanceDeposit(updated.balanceDeposit)
      setDepositsDirty(
        !isEqual(originalInitialDeposit, updated.initialDeposit) ||
          !isEqual(originalBalanceDeposit, updated.balanceDeposit)
      )
    },
    [originalBalanceDeposit, originalInitialDeposit]
  )

  const onSubjectToFinanceUpdated = useCallback(
    (updatedSubjectToFinance: FormCondition | undefined) => {
      setSubjectToFinance(updatedSubjectToFinance)
      setSubjectToFinanceDirty(
        !isEqual(contractDates?.subjectToFinance, updatedSubjectToFinance)
      )
    },
    [contractDates?.subjectToFinance]
  )

  const onBuildingAndPestConditionUpdated = useCallback(
    (updatedBuildingAndPestCondition: FormCondition | undefined) => {
      setBuildingAndPestCondition(updatedBuildingAndPestCondition)
      setBuildingAndPestConditionDirty(
        !isEqual(
          contractDates?.buildingAndPestCondition,
          updatedBuildingAndPestCondition
        )
      )
    },
    [contractDates?.buildingAndPestCondition]
  )

  const onBodyCorporateRecordsConditionUpdated = useCallback(
    (updatedBodyCorporateRecordsCondition: FormCondition | undefined) => {
      setBodyCorporateRecordsCondition(updatedBodyCorporateRecordsCondition)
      setBodyCorporateRecordsConditionDirty(
        !isEqual(
          contractDates?.bodyCorporateRecordsCondition,
          updatedBodyCorporateRecordsCondition
        )
      )
    },
    [contractDates?.bodyCorporateRecordsCondition]
  )

  const onSettlementInfoUpdated = useCallback(
    (updatedSettlementInfo: FormCondition | undefined) => {
      setSettlementInfo(updatedSettlementInfo)
      setSettlementInfoDirty(
        !isEqual(contractSettlement?.settlementInfo, updatedSettlementInfo)
      )
    },
    [contractSettlement?.settlementInfo]
  )

  const onHasSpecialConditionsUpdated = useCallback(
    (updatedHasSpecialConditions: boolean) => {
      setHasSpecialConditions(updatedHasSpecialConditions)

      if (!updatedHasSpecialConditions) {
        onSpecialConditionsUpdated([])
      }
    },
    [onSpecialConditionsUpdated]
  )

  const onSaveCurrentPage = useCallback(async () => {
    if (!contractId) return

    if (contractDateDirty || salePriceDirty) {
      const request: StaffUpdateContractComplianceFieldsRequest = {
        contractDate,
        salePrice,
      }

      const response = await staffUpdateContractComplianceFields({
        contractId,
        body: request,
      })

      if (response?.success) {
        await invalidateContractDates(campaignId, contractId)
        await invalidateSaleInformation(campaignId, contractId)
        setContractDate(response.contractDate)
        setSalePrice(response.salePrice)
        setContractDateDirty(false)
        setSalePriceDirty(false)
      }
    }

    if (
      subjectToFinanceDirty ||
      buildingAndPestConditionDirty ||
      bodyCorporateRecordsConditionDirty
    ) {
      const request: StaffUpdateContractDatesRequest = {
        contractDate,
        coolingOffDetails: contractDates?.coolingOffDetails,
        waiverOfCoolingOff: contractDates?.waiverOfCoolingOff,
        subjectToFinance,
        buildingAndPestCondition,
        bodyCorporateRecordsCondition,
      }

      const response = await staffUpdateContractDates({
        contractId,
        body: request,
      })

      if (response?.success) {
        await invalidateContractDates(campaignId, contractId)
        setSubjectToFinanceDirty(false)
        setBuildingAndPestConditionDirty(false)
        setBodyCorporateRecordsConditionDirty(false)
      }
    }

    if (settlementInfoDirty) {
      const request: StaffUpdateContractSettlementRequest = {
        settlementInfo,
        signboardRemoval: contractSettlement?.signboardRemoval,
      }

      const response = await staffUpdateContractSettlement({
        contractId,
        body: request,
      })

      if (response?.success) {
        await invalidateContractSettlement(campaignId, contractId)
        setSettlementInfoDirty(false)
      }
    }

    if (specialConditionsDirty) {
      const response = await staffUpdateContractSpecialConditions({
        contractId,
        body: specialConditions,
      })

      if (response?.success) {
        await invalidateSpecialConditions(campaignId, contractId)
        setSpecialConditionsDirty(false)
      }
    }

    if (depositsDirty) {
      const response = await staffUpdateContractDeposits({
        contractId,
        body: {
          deposits: [initialDeposit, balanceDeposit].filter(Boolean) as Deposit[],
        },
      })

      if (response?.success) {
        await invalidateContractDeposits(campaignId, contractId)
        setDepositsDirty(false)
      }
    }
  }, [
    balanceDeposit,
    bodyCorporateRecordsCondition,
    bodyCorporateRecordsConditionDirty,
    buildingAndPestCondition,
    buildingAndPestConditionDirty,
    campaignId,
    contractDate,
    contractDateDirty,
    contractDates?.bodyCorporateRecordsCondition,
    contractDates?.buildingAndPestCondition,
    contractDates?.coolingOffDetails,
    contractDates?.waiverOfCoolingOff,
    contractSettlement?.signboardRemoval,
    contractId,
    invalidateContractDates,
    invalidateContractSettlement,
    invalidateContractDeposits,
    invalidateSaleInformation,
    invalidateSpecialConditions,
    initialDeposit,
    depositsDirty,
    salePrice,
    salePriceDirty,
    settlementInfo,
    settlementInfoDirty,
    specialConditions,
    specialConditionsDirty,
    staffUpdateContractComplianceFields,
    staffUpdateContractDates,
    staffUpdateContractDeposits,
    staffUpdateContractSettlement,
    staffUpdateContractSpecialConditions,
    subjectToFinance,
    subjectToFinanceDirty,
  ])

  const currentContractDate = contractDateDirty
    ? contractDate
    : (contractDate ?? contractDates?.contractDate)
  const currentSalePrice = salePriceDirty
    ? salePrice
    : (salePrice ?? saleInformation?.salePrice)
  const currentSubjectToFinance = subjectToFinanceDirty
    ? subjectToFinance
    : (subjectToFinance ?? contractDates?.subjectToFinance)
  const currentBuildingAndPestCondition = buildingAndPestConditionDirty
    ? buildingAndPestCondition
    : (buildingAndPestCondition ?? contractDates?.buildingAndPestCondition)
  const currentBodyCorporateRecordsCondition = bodyCorporateRecordsConditionDirty
    ? bodyCorporateRecordsCondition
    : (bodyCorporateRecordsCondition ??
      contractDates?.bodyCorporateRecordsCondition)
  const currentSettlementInfo = settlementInfoDirty
    ? settlementInfo
    : (settlementInfo ?? contractSettlement?.settlementInfo)
  const currentInitialDeposit = depositsDirty
    ? initialDeposit
    : (initialDeposit ?? originalInitialDeposit)
  const currentBalanceDeposit = depositsDirty
    ? balanceDeposit
    : (balanceDeposit ?? originalBalanceDeposit)
  const currentSpecialConditions = specialConditions
  // const currentSpecialConditionsValid = useMemo(
  //   () =>
  //     areSpecialConditionsValid(
  //       currentSpecialConditions,
  //       specialConditionTemplates
  //     ),
  //   [currentSpecialConditions, specialConditionTemplates]
  // )

  const renderPage = useCallback(
    (props: TaskModalPageProps) =>
      <ContractCompliancePage
        {...props}
        contractDate={currentContractDate}
        onContractDateUpdated={onContractDateUpdated}
        salePrice={currentSalePrice}
        onSalePriceUpdated={onSalePriceUpdated}
        subjectToFinance={currentSubjectToFinance}
        onSubjectToFinanceUpdated={onSubjectToFinanceUpdated}
        buildingAndPestCondition={currentBuildingAndPestCondition}
        onBuildingAndPestConditionUpdated={onBuildingAndPestConditionUpdated}
        bodyCorporateRecordsCondition={currentBodyCorporateRecordsCondition}
        onBodyCorporateRecordsConditionUpdated={
          onBodyCorporateRecordsConditionUpdated
        }
        settlementInfo={currentSettlementInfo}
        onSettlementInfoUpdated={onSettlementInfoUpdated}
        initialDeposit={currentInitialDeposit}
        balanceDeposit={currentBalanceDeposit}
        onDepositsUpdated={onDepositsUpdated}
        hasSpecialConditions={hasSpecialConditions}
        onHasSpecialConditionsUpdated={onHasSpecialConditionsUpdated}
        specialConditions={currentSpecialConditions}
        onSpecialConditionsUpdated={onSpecialConditionsUpdated}
      />,
    [
      currentContractDate,
      currentBalanceDeposit,
      currentBodyCorporateRecordsCondition,
      currentBuildingAndPestCondition,
      currentInitialDeposit,
      currentSalePrice,
      currentSettlementInfo,
      currentSubjectToFinance,
      currentSpecialConditions,
      onDepositsUpdated,
      onBuildingAndPestConditionUpdated,
      onBodyCorporateRecordsConditionUpdated,
      onContractDateUpdated,
      onHasSpecialConditionsUpdated,
      onSettlementInfoUpdated,
      onSubjectToFinanceUpdated,
      onSalePriceUpdated,
      onSpecialConditionsUpdated,
    ]
  )

  // const isPageInvalid = useCallback(
  //   (task: AutopilotTaskDto) => {
  //     const isGeneralComplianceTask =
  //       task.category?.taskType ===
  //       TaskCategoryTaskType.GeneralContractCompliance

  //     const requiresContractDate = Boolean(
  //       isGeneralComplianceTask &&
  //       task.taskSteps?.some(
  //         (step) => step.name === REQUIRED_CONTRACT_DATE_STEP_NAME
  //       )
  //     )
  //     const requiresSalePrice = Boolean(
  //       isGeneralComplianceTask &&
  //       task.taskSteps?.some(
  //         (step) => step.name === REQUIRED_SALE_PRICE_STEP_NAME
  //       )
  //     )
  //     const requiresInitialDeposit = Boolean(
  //       task.taskSteps?.some((step) => step.name === INITIAL_DEPOSIT_STEP_NAME)
  //     )
  //     const requiresBalanceDeposit = Boolean(
  //       task.taskSteps?.some((step) => step.name === BALANCE_DEPOSIT_STEP_NAME)
  //     )
  //     const requiresFinanceCondition = Boolean(
  //       task.taskSteps?.some((step) => step.name === FINANCE_CONDITION_STEP_NAME)
  //     )
  //     const requiresBuildingAndPest = Boolean(
  //       task.taskSteps?.some((step) => step.name === BUILDING_AND_PEST_STEP_NAME)
  //     )
  //     const requiresBodyCorporateRecords = Boolean(
  //       task.taskSteps?.some(
  //         (step) => step.name === BODY_CORPORATE_RECORDS_STEP_NAME
  //       )
  //     )
  //     const requiresSettlementDate = Boolean(
  //       task.taskSteps?.some((step) => step.name === SETTLEMENT_DATE_STEP_NAME)
  //     )
  //     const requiresSpecialConditionsAddressed = Boolean(
  //       task.taskSteps?.some((step) =>
  //         isSpecialConditionsComplianceStep(step.name)
  //       )
  //     )

  //     return (
  //       (requiresContractDate && !currentContractDate) ||
  //       (requiresSalePrice && currentSalePrice == null) ||
  //       (requiresInitialDeposit &&
  //         depositValidationError) ||
  //       (requiresBalanceDeposit &&
  //         depositValidationError) ||
  //       (requiresFinanceCondition &&
  //         financeConditionValidationError) ||
  //       (requiresBuildingAndPest &&
  //         buildingAndPestValidationError) ||
  //       (requiresBodyCorporateRecords &&
  //         bodyCorporateRecordsValidationError) ||
  //       (requiresSettlementDate &&
  //         settlementDateValidationError) ||
  //       (requiresSpecialConditionsAddressed &&
  //         hasSpecialConditions &&
  //         !currentSpecialConditionsValid)
  //     )
  //   },
  //   [
  //     currentContractDate,
  //     currentInitialDeposit,
  //     currentBalanceDeposit,
  //     depositValidationError,
  //     financeConditionValidationError,
  //     buildingAndPestValidationError,
  //     bodyCorporateRecordsValidationError,
  //     settlementDateValidationError,
  //     currentBodyCorporateRecordsCondition,
  //     currentBuildingAndPestCondition,
  //     currentSalePrice,
  //     currentSettlementInfo,
  //     currentSubjectToFinance,
  //     hasSpecialConditions,
  //     currentSpecialConditionsValid,
  //   ]
  // )

  if (!campaign) return null

  const contentConfig: TaskModalContentConfig = {
    documents: filteredDocuments,
    isPending:
      isCampaignPending ||
      documentsPending ||
      contractDatesPending ||
      contractSettlementPending ||
      contractDepositsPending ||
      saleInformationPending ||
      specialConditionsPending ||
      specialConditionBankPending,
    onSaveAllPages: async () => {
      await onSaveCurrentPage()
    },
    renderPage,
  }

  return (
    <TaskModalDialog
      title="Contract Compliance"
      tasks={tasks}
      open={isOpen}
      onClose={onClose}
      campaignId={campaignId}
      campaign={campaign}
      contentConfig={contentConfig}
    />
  )
}

export default ContractComplianceModal
